import { Referrer } from '../classes/referrer';
import { Supplier } from '../classes/supplier';

export type PartyKind = 'referrer' | 'supplier';

export interface PartyOption {
    key: string;
    kind: PartyKind;
    id: number;
    group: string;
    kindLabel: string;
    label: string;
    hint: string;
    searchText: string;
}

export function partyKey(kind: PartyKind, id: number): string {
    return kind + ':' + id;
}

function byLabel(left: PartyOption, right: PartyOption): number {
    return left.label.localeCompare(right.label, 'it', { sensitivity: 'base' }) || (left.id - right.id);
}

function toReferrerParty(referrer: Referrer): PartyOption | null {
    if (referrer.IDReferrer == null) {
        return null;
    }
    const label = `${referrer.Name || ''} ${referrer.Surname || ''}`.replace(/\s+/g, ' ').trim();
    const hint = (referrer.Email || '').trim();
    const shown = label || hint || String(referrer.IDReferrer);
    return {
        key: partyKey('referrer', referrer.IDReferrer),
        kind: 'referrer',
        id: referrer.IDReferrer,
        group: 'Segnalatori',
        kindLabel: 'Segnalatore',
        label: shown,
        hint,
        searchText: ['segnalatore', referrer.IDReferrer, shown, hint].join(' ').toLowerCase()
    };
}

function toSupplierParty(supplier: Supplier): PartyOption | null {
    if (supplier.IDSupplier == null) {
        return null;
    }
    const label = (supplier.Name || '').replace(/\s+/g, ' ').trim();
    const cf = (supplier.CF || '').trim();
    const email = (supplier.Email || '').trim();
    const hint = [cf, email].filter(part => part.length > 0).join(' · ');
    const shown = label || hint || String(supplier.IDSupplier);
    return {
        key: partyKey('supplier', supplier.IDSupplier),
        kind: 'supplier',
        id: supplier.IDSupplier,
        group: 'Fornitori',
        kindLabel: 'Fornitore',
        label: shown,
        hint,
        searchText: ['fornitore', supplier.IDSupplier, shown, cf, email].join(' ').toLowerCase()
    };
}

export function partyOptionList(referrers: Array<Referrer>, suppliers: Array<Supplier>): PartyOption[] {
    const referrerOptions = referrers
        .map(referrer => toReferrerParty(referrer))
        .filter((option): option is PartyOption => option !== null)
        .sort(byLabel);
    const supplierOptions = suppliers
        .map(supplier => toSupplierParty(supplier))
        .filter((option): option is PartyOption => option !== null)
        .sort(byLabel);
    return referrerOptions.concat(supplierOptions);
}

export function searchParty(term: string, item: PartyOption): boolean {
    return item.searchText.indexOf((term || '').trim().toLowerCase()) !== -1;
}

export function splitPartyKeys(keys: string[] | null | undefined): { referrerIds: number[]; supplierIds: number[] } {
    const referrerIds: number[] = [];
    const supplierIds: number[] = [];
    (keys || []).forEach(key => {
        const match = /^(referrer|supplier):(\d+)$/.exec(String(key));
        if (!match) {
            return;
        }
        const id = Number(match[2]);
        if (match[1] === 'referrer') {
            referrerIds.push(id);
        } else {
            supplierIds.push(id);
        }
    });
    return { referrerIds, supplierIds };
}
